import { useCallback, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type FormItem } from "@njbsoft/form-logic";
import { FormLetterhead, FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import { resolveFormTheme } from "@njbsoft/form-ui";
import {
  Badge,
  Button,
  Card,
  EmptyValue,
  PageHeader,
  PageSkeleton,
  portalStatusVariant,
} from "@/components/ui";
import { RequiresConnection } from "@/components/requires-connection";
import { HistoryChangeRequestsCard } from "@/components/tester/change-requests/history-change-requests-card";
import {
  onlineRosterTarget,
  prestageResubmission,
} from "@/components/tester/change-requests/staging/prestage-resubmission";
import {
  submissionOutcomeLabelKey,
  submissionOutcomeVariant,
  templateTitleOrFallback,
} from "@/components/tester/history/history-columns";
import {
  SubmissionAttachmentGallery,
  type FieldSubmissionAttachmentMeta,
} from "@/components/tester/history/submission-attachment-gallery";
import { SuspendedAuthorizationNotice } from "@/components/tester/history/suspended-authorization-notice";
import { StartTestDialog } from "@/components/tester/start-test/start-test-dialog";
import { useIsOnline } from "@/hooks/use-is-online";
import { useMySubmission } from "@/hooks/use-my-submission";
import { useFieldMe } from "@/hooks/use-persona";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime, formatPerformedAt } from "@/lib/dates";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";

export const Route = createFileRoute("/_company/history/$submissionId")({
  component: TesterSubmissionDetailPage,
});

/**
 * One of the tester's own submitted documents, reopened.
 *
 * THE DOCUMENT IS RENDERED SEALED, and the mechanism is a single OMITTED prop.
 * `FormRenderer` treats an absent `onAnswerChange` as "this mount is displaying
 * a finished document rather than collecting one", and that one fact is what
 * makes it read the outcome and every computed value SEALED INTO the responses
 * at submit instead of re-deriving them from today's evaluator. Adding a
 * handler here — even a no-op, even "for symmetry" — silently converts a signed
 * regulatory record into a live recomputation, with nothing failing to say so.
 * The suite asserts the prop's absence for exactly that reason.
 *
 * The chrome around it prints. The page gutter collapses for print because on
 * paper it would sit INSIDE the stylesheet's `@page` margin and push the
 * document a further 2rem in on every edge of every sheet; the identity list and
 * the actions drop out, leaving the letterhead + document alone on the sheet.
 */
function TesterSubmissionDetailPage() {
  const { t, i18n } = useTranslation(["field-work", "common", "form-renderer", "form-submissions"]);
  const { submissionId } = Route.useParams();
  const [resubmitOpen, setResubmitOpen] = useState(false);

  const isOnline = useIsOnline();
  const { storage } = useFieldworkStorage();
  const toast = useToast();
  const meQuery = useFieldMe();
  const { data: detail, isLoading, isError } = useMySubmission(submissionId);

  // ONE successor read, issued only when this rejected row carries the chain's
  // forward link: the redo that answered it, whose date and verdict the banner
  // states instead of leaving the reader to hunt the row that replaced this
  // one. No chain walk — one step is the whole iteration view. The hook is
  // disabled without an id, so every other document mounts it as a no-op.
  const supersededBy =
    detail?.status === "rejected" ? (detail.superseded_by_submission_id ?? null) : null;
  const successorQuery = useMySubmission(supersededBy);
  const successor = supersededBy === null ? null : (successorQuery.data ?? null);

  const attachmentsByLinkId = useMemo(() => {
    const map = new Map<string, FieldSubmissionAttachmentMeta[]>();
    for (const att of detail?.attachments ?? []) {
      const bucket = map.get(att.link_id);
      if (bucket) {
        bucket.push(att);
      } else {
        map.set(att.link_id, [att]);
      }
    }
    return map;
  }, [detail?.attachments]);

  const evidenceComplete = detail?.attachments_complete ?? true;

  const attachmentSlot = useCallback(
    (item: FormItem) => (
      <SubmissionAttachmentGallery
        attachments={attachmentsByLinkId.get(item.link_id) ?? []}
        evidenceComplete={evidenceComplete}
      />
    ),
    [attachmentsByLinkId, evidenceComplete],
  );

  // Every datum on this page is a server round trip: the archive is not carried
  // in the fieldwork bundle, and caching an authenticated API response in the
  // service worker is forbidden on this portal. Rendering the chrome over a
  // failing read would be the working-looking lie the panel exists to replace.
  if (!isOnline) {
    return (
      <RequiresConnection
        showTesterCta={false}
        title={t("field-work:history.offline.title")}
        body={t("field-work:history.offline.body")}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Visibility is undifferentiated 404: another tester's document, a
  // nonexistent id, and the caller's own document in an org whose grant lapsed
  // all answer identically, so there is nothing more specific to branch on.
  if (isError || !detail) {
    return (
      <div className="space-y-4 p-4 sm:p-8">
        <PageHeader title={t("field-work:history.detail.title")} />
        <p className="text-sm text-muted">{t("field-work:history.detail.notFound")}</p>
        <Link
          to="/my-work"
          search={{ tab: "completed" }}
          className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
        >
          {t("field-work:history.detail.back")}
        </Link>
      </div>
    );
  }

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";

  // The wire definition is an opaque JSON object; the pinned version passed the
  // server's publish gates, but guard on `sections` before handing the document
  // to the renderer.
  const definition = parseFormDefinition(detail.definition);
  const renderable = Array.isArray(definition.sections);

  const formTitle = templateTitleOrFallback(t, detail.template_title);
  // Severed together with the title by the same LEFT JOIN, so it takes the same
  // stand-in rather than leaving the letterhead's meta line blank.
  const versionLine =
    detail.template_version === null || detail.template_version === undefined
      ? templateTitleOrFallback(t, null)
      : `${t("field-work:history.detail.version")} ${String(detail.template_version)}`;

  const hasGps = typeof detail.gps_lat === "number" && typeof detail.gps_lng === "number";

  return (
    <div className="p-4 sm:p-8 print:p-0">
      <div className="print:hidden">
        <PageHeader
          title={t("field-work:history.detail.title")}
          actions={
            <Button variant="secondary" className="min-h-11" onClick={() => window.print()}>
              {t("field-work:history.detail.print")}
            </Button>
          }
        />
        <div className="mt-2">
          <Link
            to="/my-work"
            search={{ tab: "completed" }}
            className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
          >
            {t("field-work:history.detail.back")}
          </Link>
        </div>
      </div>

      <div className="mt-6 space-y-6 print:mt-0 print:space-y-0">
        <div className="print:hidden">
          <SuspendedAuthorizationNotice authorizations={meQuery.data?.authorizations ?? []} />
        </div>

        {/* THE REASON IS QUOTED VERBATIM. `rejection_reason` is the reviewer's
            own sentence and the only thing on this page that tells the tester
            what to change; the fs_chk_rejection CHECK permits a reason only
            while the row is rejected, so a value here always means this
            document came back and why. The CODE's label and cure sit ABOVE the
            notes, read off the shared rejection family so this portal's copy
            localizes — a code the CHECK admits as null leaves the notes alone.
            Resubmitting reuses the start path — a correction is a new test,
            and the same gauge and readiness gate apply to it as to any other —
            and pre-stages the device panel once the start lands: the rejection
            pinned, and any request the test returned staged again.

            ANSWERED rejections trade the cure for the chain's forward link: a
            rejected row carrying `superseded_by_submission_id` has its redo
            already in, so the banner states the resubmission (linked to its
            detail) instead of offering a second Resubmit beside it. Until that
            one read settles — loading or failed — the banner renders exactly
            as it always did, cure button included: an unavailable successor
            read must never strand the tester off the cure path. The
            "awaiting review" tail is claimed only while the successor truly
            sits in review; a decided redo links without the tail, and its own
            detail page carries its verdict. */}
        {detail.status === "rejected" ? (
          <div role="alert" className="rounded-lg bg-red-100 p-4 print:hidden dark:bg-red-900">
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              {detail.reviewed_at === null || detail.reviewed_at === undefined
                ? t("field-work:history.detail.rejected.title")
                : t("field-work:history.detail.rejected.titleAt", {
                    date: formatDateTime(detail.reviewed_at),
                  })}
            </p>
            {supersededBy !== null && successor !== null ? (
              <p className="mt-1 text-sm font-medium text-red-800 dark:text-red-300">
                <Link
                  to="/history/$submissionId"
                  params={{ submissionId: supersededBy }}
                  className="underline hover:no-underline max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center"
                >
                  {successor.status === "submitted" || successor.status === "needs_review"
                    ? t("field-work:history.detail.rejected.resubmittedAwaitingReview", {
                        time: formatPerformedAt(successor.performed_at, successor.performed_date),
                      })
                    : t("field-work:history.detail.rejected.resubmitted", {
                        time: formatPerformedAt(successor.performed_at, successor.performed_date),
                      })}
                </Link>
              </p>
            ) : null}
            {detail.rejection_reason_code ? (
              <p className="mt-1 text-sm font-medium text-red-800 dark:text-red-300">
                {t("form-submissions:review.rejectedWith", {
                  label: t(
                    `form-submissions:rejectionReason.${detail.rejection_reason_code}.label`,
                  ),
                  cure: t(`form-submissions:rejectionReason.${detail.rejection_reason_code}.cure`),
                })}
              </p>
            ) : null}
            <p className="mt-1 text-sm text-red-800 dark:text-red-300">
              {detail.rejection_reason ?? t("field-work:home.rejections.noReason")}
            </p>
            {supersededBy === null || successor === null ? (
              <Button
                className="mt-3 min-h-11"
                onClick={() => {
                  setResubmitOpen(true);
                }}
              >
                {t("field-work:history.detail.rejected.resubmit")}
              </Button>
            ) : null}
          </div>
        ) : null}

        {resubmitOpen ? (
          <StartTestDialog
            scopeAssignmentId={detail.assignment_id}
            supersedesSubmissionId={detail.id}
            targetKind={detail.target_kind}
            beforeOpen={async () => {
              if (storage === null) throw new Error("fieldwork_storage_not_ready");
              // Online, a target this device never synced resolves through the
              // server roster read instead of refusing; each remaining refusal
              // names its cure: the server read failed or came back empty, or
              // the other tab owns the draft until it closes.
              const refusal = await prestageResubmission(
                storage,
                detail,
                isOnline ? onlineRosterTarget(detail) : undefined,
              );
              if (refusal === "target_not_on_device") {
                toast.error("change-requests:history.syncFirst");
              }
              if (refusal === "draft_open_elsewhere") toast.warning("company:pwa.draftLock.title");
              return refusal === null;
            }}
            onClose={() => {
              setResubmitOpen(false);
            }}
          />
        ) : null}

        <div className="print:hidden">
          <Card title={t("field-work:history.detail.identityTitle")}>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted">{t("field-work:history.detail.template")}</dt>
                <dd className="mt-0.5 text-on-surface">{formTitle}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.detail.version")}</dt>
                <dd className="mt-0.5 text-on-surface">{versionLine}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.detail.facility")}</dt>
                <dd className="mt-0.5 text-on-surface">{detail.facility_name ?? <EmptyValue />}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.detail.deviceSerial")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {detail.device_serial_number ? (
                    <span className="font-mono text-xs">{detail.device_serial_number}</span>
                  ) : (
                    <EmptyValue />
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.detail.performedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {formatPerformedAt(detail.performed_at, detail.performed_date)}
                </dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.detail.receivedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">{formatDateTime(detail.received_at)}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("field-work:history.columns.status")}</dt>
                <dd className="mt-0.5">
                  <Badge variant={portalStatusVariant(detail.status)}>
                    {t(`field-work:history.status.${detail.status}`)}
                  </Badge>
                </dd>
              </div>
              {detail.outcome && (
                <div>
                  <dt className="text-muted">{t("field-work:history.detail.outcome")}</dt>
                  <dd className="mt-0.5">
                    <Badge variant={submissionOutcomeVariant[detail.outcome]}>
                      {t(submissionOutcomeLabelKey(detail.target_kind, detail.outcome))}
                    </Badge>
                  </dd>
                </div>
              )}
            </dl>
            {/* The persisted acceptance-requirements evaluation, as the server
                wrote it: each checked requirement's label and its OWN cure
                sentence, quoted verbatim — never re-derived here. The cure is
                empty unless the requirement failed, so a failing entry is the
                one that says WHY this document routed the way it did, and it
                carries the red the rejection banner above already taught. */}
            {(detail.requirements ?? []).length > 0 && (
              <ul className="mt-4 space-y-1 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
                {detail.requirements?.map((requirement) => (
                  <li
                    key={requirement.id}
                    className={requirement.cure ? "text-danger" : "text-muted"}
                  >
                    {requirement.cure
                      ? `${t(`field-work:requirements.${requirement.id}`)}: ${requirement.cure}`
                      : t(`field-work:requirements.${requirement.id}`)}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <HistoryChangeRequestsCard
          links={detail.change_requests ?? []}
          testSerial={detail.device_serial_number ?? null}
        />

        {/* PRINTS, unlike the chrome around it. An incomplete evidence set
            leaves the row at `submitted` with a null outcome — the resting
            state of a complete document awaiting review — so nothing else on
            the page or the sheet distinguishes the two. The CSV and XLSX
            exports disclose it; a printed record that does not would be the
            only artifact of this document that lies. */}
        {!detail.attachments_complete && (
          <div role="status" className="rounded-lg bg-amber-100 p-3 dark:bg-amber-900">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {t("field-work:history.detail.evidence.incompleteTitle")}
            </p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
              {t("field-work:history.detail.evidence.incompleteBody", {
                uploaded: detail.attachments.length,
                expected: detail.expected_attachment_count,
              })}
            </p>
          </div>
        )}

        <div
          className="form-doc overflow-clip rounded-xl border border-[var(--form-card-border)] shadow-sm print:rounded-none print:border-0 print:shadow-none"
          data-form-theme={resolveFormTheme(definition)}
        >
          {/* `logoUrl` is pinned null: the org-logo endpoint authenticates as an
              org member and a field tester is not one, so the request would be a
              guaranteed 403. The monogram is the designed identity here. */}
          <FormLetterhead
            orgName={detail.org_name ?? ""}
            programLine={t("form-renderer:letterhead.programLine")}
            logoUrl={null}
            formTitle={formTitle}
            metaLine={versionLine}
          />
          <div className="bg-[var(--form-canvas)] p-4 sm:p-6">
            {renderable ? (
              <FormRenderer
                definition={definition}
                answers={detail.responses}
                language={language}
                attachmentSlot={attachmentSlot}
              />
            ) : (
              <p className="text-sm text-muted">
                {t("field-work:history.detail.documentUnavailable")}
              </p>
            )}
          </div>
        </div>

        {hasGps && (
          <div className="print:hidden">
            <Card title={t("field-work:history.detail.gps.title")}>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted">{t("field-work:history.detail.gps.lat")}</dt>
                  <dd className="mt-0.5 font-mono text-on-surface">{detail.gps_lat?.toFixed(6)}</dd>
                </div>
                <div>
                  <dt className="text-muted">{t("field-work:history.detail.gps.lng")}</dt>
                  <dd className="mt-0.5 font-mono text-on-surface">{detail.gps_lng?.toFixed(6)}</dd>
                </div>
                <div>
                  <dt className="text-muted">{t("field-work:history.detail.gps.accuracy")}</dt>
                  <dd className="mt-0.5 font-mono text-on-surface">
                    {typeof detail.gps_accuracy_m === "number" ? (
                      detail.gps_accuracy_m.toFixed(1)
                    ) : (
                      <EmptyValue />
                    )}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
